import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import {
  canUseApiKeyFromEnv,
  effectiveGuardAuthMode,
  isGuardSessionReady,
  requiresUserLogin,
} from '@shared/lib/guardAuth';
import { NocLayout } from './shell/NocLayout';
import { LoginPage } from '@features/auth/Login';
import { ProvisionPage } from '@features/auth/Provision';
import { NocDashboard } from '@features/noc/NocDashboard';
import { IncidentDetailPage } from '@features/noc/IncidentDetail';

function RequireAuth(): JSX.Element {
  if (!isGuardSessionReady()) {
    return <Navigate to={requiresUserLogin() ? '/login' : '/provision'} replace />;
  }
  return <Outlet />;
}

function GuestOnlyLogin(): JSX.Element {
  if (isGuardSessionReady()) return <Navigate to="/" replace />;
  if (!requiresUserLogin()) {
    return <Navigate to={canUseApiKeyFromEnv() ? '/' : '/provision'} replace />;
  }
  return <LoginPage />;
}

function NocBoard(): JSX.Element {
  return (
    <>
      <NocDashboard />
      <Outlet />
    </>
  );
}

function GuestOnlyProvision(): JSX.Element {
  if (isGuardSessionReady()) return <Navigate to="/" replace />;
  if (effectiveGuardAuthMode() === 'user') {
    return <Navigate to="/login" replace />;
  }
  return <ProvisionPage />;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <GuestOnlyLogin />,
  },
  {
    path: '/provision',
    element: <GuestOnlyProvision />,
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <NocLayout />,
        children: [
          {
            // NocBoard keeps the dashboard mounted at all times so polling,
            // the alarm loop and the live tile counters never pause — even
            // while a guard is drilling into a specific incident.
            element: <NocBoard />,
            children: [
              { index: true, element: null },
              { path: 'incidents/:logId', element: <IncidentDetailPage /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
